import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { useCombobox, useMultipleSelection } from 'downshift'
import { Button, Chip, Dropdown, Field, Input, Label, ValidationMessage } from '@digdir/designsystemet-react'
import { translate, type Locale, type Vars } from '@/shared/i18n'
import type { TKey } from '@/locales/keys'
import { Icon } from './Icon'

// ---------------------------------------------------------------------------
// Combobox — searchable single-select / multiselect field (props in → UI out).
//
// A typeahead built on the `downshift` headless library: the caller passes a
// flat list of options and a controlled value, and the component owns the
// filter text, the open/highlight state, and all aria wiring. Two modes share
// one component, discriminated by the `multiple` prop:
//   - single (`multiple` omitted/false): value is `string | null`; selecting
//     an option fills the input with its label and closes; clearable.
//   - multi  (`multiple: true`): value is `string[]`; selected options render
//     as removable chips, Backspace removes the last, and chosen options stay
//     in the list marked selected (standard multiselect UX).
//
// DS owns the controls and native manual-popover surface; `.field*`, `.dd*`
// and `.cbx*` classes remain layout hooks. Filtering
// is a client-side case-insensitive substring match against `option.label`;
// pass `onInputChange` to additionally drive server-side filtering.
//
// Dumb and surface-agnostic: the caller supplies already-translated
// label/placeholder/hint/error text and owns the value via `onChange`. No
// fetching, no privileged logic, no Norwegian strings — safe for the public,
// sessionless register bundle.
// ---------------------------------------------------------------------------

export interface ComboboxOption {
  /** Stable value emitted through onChange (also the React list key). */
  value: string
  /** Visible label (pre-translated by the caller); filtered against this. */
  label: string
  /** When true the option is shown greyed and cannot be selected. */
  disabled?: boolean
}

interface ComboboxCommonProps {
  /** Visible label text (pre-translated by caller). */
  label: string
  /** The selectable options. */
  options: ComboboxOption[]
  /** Placeholder shown in the empty input. */
  placeholder?: string
  /** Optional helper text below the field. */
  hint?: string
  /** Validation error message. Triggers aria-invalid. */
  error?: string
  /** Disables the whole control. */
  disabled?: boolean
  /** Announces a required selection; the caller validates selection, not filter text. */
  required?: boolean
  /** Optional native input id (otherwise generated by downshift). */
  id?: string
  /** Control/status language. Standalone-safe; callers supply their active locale. */
  locale?: Locale
  /**
   * Notified once per query edit, never for selection/owner synchronization.
   * Wire this when the caller also drives server-side filtering of `options`.
   */
  onInputChange?: (input: string) => void
}

interface ComboboxSingleProps extends ComboboxCommonProps {
  multiple?: false
  /** Controlled selected value, or null when nothing is selected. */
  value: string | null
  /** Called with the new value (null when cleared). */
  onChange: (value: string | null) => void
}

interface ComboboxMultipleProps extends ComboboxCommonProps {
  multiple: true
  /** Controlled selected values. */
  value: string[]
  /** Called with the full new array of selected values. */
  onChange: (values: string[]) => void
}

// Discriminated union on `multiple` so TS enforces the value/onChange shape
// per mode (string | null vs string[]) — no `any`.
export type ComboboxProps = ComboboxSingleProps | ComboboxMultipleProps

// Case-insensitive substring match against the option label.
function filterOptions(options: ComboboxOption[], input: string): ComboboxOption[] {
  const needle = input.trim().toLowerCase()
  if (!needle) return options
  return options.filter((o) => o.label.toLowerCase().includes(needle))
}

function message(locale: Locale, key: 'open' | 'close' | 'clear' | 'remove' | 'results' | 'selected', vars?: Vars) {
  return translate(locale, `chrome.combobox.${key}` as TKey, vars)
}

// Retain only the owner's selected labels, not a cache of transient search results.
function useSelectedOptions(options: ComboboxOption[], values: string[]) {
  const retained = useRef(new Map<string, ComboboxOption>())
  const selected = values.map(value => options.find(option => option.value === value) ?? retained.current.get(value))
    .filter((option): option is ComboboxOption => !!option)
  useLayoutEffect(() => {
    retained.current = new Map(selected.map(option => [option.value, option]))
  })
  return selected
}

// Downshift owns dismissal. DS's uncontrolled close requests must not compete
// with its toggle/selection listeners or close a stay-open multiselect surface.
function useMenuSurface(open: boolean, items: ComboboxOption[], highlightedIndex: number) {
  const anchorRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)
  const [host, setHost] = useState<Element>(document.body)
  useLayoutEffect(() => {
    setHost(anchorRef.current?.closest('[popover],dialog') ?? document.body)
  }, [])
  const place = useCallback(() => {
    const menu = menuRef.current
    const anchor = anchorRef.current
    if (!menu?.matches(':popover-open') || !anchor) return
    const rect = anchor.getBoundingClientRect()
    const below = Math.max(0, window.innerHeight - rect.bottom - 12)
    const above = Math.max(0, rect.top - 12)
    const onTop = below < Math.min(260, menu.scrollHeight) && above > below
    const width = Math.min(rect.width, Math.max(0, window.innerWidth - 16))
    Object.assign(menu.style, {
      width: `${width}px`,
      maxHeight: `${Math.min(260, onTop ? above : below)}px`,
      left: `${Math.max(8, Math.min(rect.left, window.innerWidth - width - 8))}px`,
    })
    const height = menu.getBoundingClientRect().height
    menu.style.top = `${Math.max(8, Math.min(onTop ? rect.top - height - 4 : rect.bottom + 4, window.innerHeight - height - 8))}px`
  }, [])
  useEffect(() => {
    if (!open) return
    // DS opens in its passive effect, before this post-open measurement.
    place()
    if (menuRef.current?.matches(':popover-open')) menuRef.current.children[highlightedIndex]?.scrollIntoView?.({ block: 'nearest' })
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, host, items, highlightedIndex, place])
  return { anchorRef, menuRef, host, place }
}

function escapeKey(event: KeyboardEvent<HTMLElement> & { preventDownshiftDefault?: boolean }, open: boolean) {
  if (event.key !== 'Escape') return
  if (open) event.stopPropagation()
  // Closed-picker Escape belongs to the containing dialog/popover, even when
  // there is a selected label/query. Downshift otherwise clears it and consumes Esc.
  else event.preventDownshiftDefault = true
}

const menuStyle = {
  position: 'fixed', inset: 'auto', translate: 'none', margin: 0,
  listStyle: 'none', minWidth: 0, overflowY: 'auto',
} as const

/**
 * Searchable single-select / multiselect combobox.
 *
 * @example // single
 * <Combobox
 *   label={t('case.municipality')}
 *   options={municipalities}
 *   value={value}
 *   onChange={setValue}
 *   placeholder={t('common.search')}
 * />
 *
 * @example // multiselect
 * <Combobox
 *   multiple
 *   label={t('case.tradeGroups')}
 *   options={tradeGroups}
 *   value={values}
 *   onChange={setValues}
 * />
 */
export function Combobox(props: ComboboxProps) {
  return props.multiple ? (
    <MultiCombobox {...props} />
  ) : (
    <SingleCombobox {...props} />
  )
}

// ── Single-select ──────────────────────────────────────────────────────────

function SingleCombobox({
  label,
  options,
  value,
  onChange,
  placeholder,
  hint,
  error,
  disabled = false,
  required,
  id,
  locale = 'nb',
  onInputChange,
}: ComboboxSingleProps) {
  const baseId = useId()
  const errorId = `${id ?? baseId}err`

  const selectedOption = useSelectedOptions(options, value == null ? [] : [value])[0] ?? null

  const [inputValue, setInputValue] = useState(selectedOption?.label ?? '')
  const items = useMemo(() => filterOptions(options, inputValue), [options, inputValue])

  const {
    isOpen,
    highlightedIndex,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    getToggleButtonProps,
    closeMenu,
  } = useCombobox<ComboboxOption>({
    inputId: id,
    items,
    selectedItem: selectedOption,
    inputValue,
    itemToString: (item) => item?.label ?? '',
    // Identify options by their stable `value`, not by object identity.
    // Callers routinely rebuild `options` inline on every render, which under
    // the default identity comparison makes downshift read each render as a
    // fresh selection and reset the input back to the selected label —
    // discarding whatever the user was typing.
    itemToKey: (item) => item?.value ?? null,
    isItemDisabled: (item) => disabled || Boolean(item.disabled),
    onInputValueChange: ({ inputValue: next, type }) => {
      if (type === useCombobox.stateChangeTypes.InputChange) {
        setInputValue(next ?? '')
        onInputChange?.(next ?? '')
      } else if (type === useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem) {
        setInputValue(next ?? '')
      } else {
        setInputValue(selectedOption?.label ?? '')
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      onChange(selectedItem ? selectedItem.value : null)
      setInputValue(selectedOption?.label ?? '')
    },
  })

  const previousSelection = useRef(selectedOption)
  useEffect(() => {
    const previous = previousSelection.current
    if (previous?.value !== selectedOption?.value || previous?.label !== selectedOption?.label) {
      setInputValue(current => previous?.value !== selectedOption?.value || current === previous?.label
        ? selectedOption?.label ?? '' : current)
    }
    previousSelection.current = selectedOption
  }, [selectedOption])
  const expanded = isOpen && !disabled
  const surface = useMenuSurface(expanded, items, highlightedIndex)
  useEffect(() => { if (disabled) closeMenu() }, [disabled, closeMenu])

  return (
    <Field className="field">
      <Label className="field__lbl" {...getLabelProps()}>
        {label}
      </Label>
      {hint && <Field.Description id={`${id ?? baseId}-hint`} className="field__hint">{hint}</Field.Description>}

      <div ref={surface.anchorRef} className={clsx('cbx', error && 'cbx--invalid')}>
        <Input
          className="cbx__input"
          placeholder={placeholder}
          aria-required={required || undefined}
          aria-invalid={error ? 'true' : undefined}
          {...getInputProps({ disabled, onKeyDown: event => escapeKey(event, expanded) })}
        />
        {value != null && !disabled && (
          <Button variant="tertiary" icon
            type="button"
            className="cbx__clear"
            aria-label={message(locale, 'clear', { label })}
            onMouseDown={event => event.preventDefault()}
            onClick={() => { setInputValue(selectedOption?.label ?? ''); onChange(null) }}
          >
            <Icon name="x" size={18} aria-hidden="true" />
          </Button>
        )}
        <Button variant="tertiary" icon
          type="button"
          className="cbx__toggle"
          {...getToggleButtonProps({ disabled, 'aria-label': message(locale, expanded ? 'close' : 'open', { label }) })}
        >
          <Icon name="chevronD" size={18} aria-hidden="true" />
        </Button>
      </div>

      {createPortal(
        <Dropdown asChild open={expanded} placement="none" onToggle={surface.place} style={menuStyle}>
          <ul className="dd__menu cbx__menu" {...getMenuProps({ ref: surface.menuRef })}>
            {expanded && items.map((item, index) => (
              <Button asChild variant="tertiary" key={item.value}>
                <li
                  className={clsx(
                    'dd__item',
                    index === highlightedIndex && 'dd__item--highlight',
                    item.value === value && 'dd__item--active',
                    item.disabled && 'dd__item--disabled',
                  )}
                  {...getItemProps({ item, index })}
                  aria-selected={item.value === value}
                  data-active={index === highlightedIndex || undefined}
                >
                  <span className="dd__item-text">{item.label}</span>
                </li>
              </Button>
            ))}
          </ul>
        </Dropdown>, surface.host,
      )}
      <span className="sr-only" role="status">
        {expanded && message(locale, 'results', { count: items.length })} {message(locale, 'selected', { count: value == null ? 0 : 1 })}
      </span>

      {error && (
        <ValidationMessage id={errorId} className="field__err" role="alert">
          {error}
        </ValidationMessage>
      )}
    </Field>
  )
}

// ── Multiselect ────────────────────────────────────────────────────────────

function MultiCombobox({
  label,
  options,
  value,
  onChange,
  placeholder,
  hint,
  error,
  disabled = false,
  required,
  id,
  locale = 'nb',
  onInputChange,
}: ComboboxMultipleProps) {
  const baseId = useId()
  const errorId = `${id ?? baseId}err`

  const selectedItems = useSelectedOptions(options, value)

  const [inputValue, setInputValue] = useState('')

  // The menu keeps already-selected options visible (marked) — standard
  // multiselect UX — so only the text filter narrows the list.
  const items = useMemo(() => filterOptions(options, inputValue), [options, inputValue])

  const { getSelectedItemProps, getDropdownProps, removeSelectedItem } =
    useMultipleSelection<ComboboxOption>({
      selectedItems,
      itemToKey: item => item?.value ?? null,
      stateReducer: (state, { changes }) => disabled ? state : changes,
      onStateChange: ({ selectedItems: next, type }) => {
        switch (type) {
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            onChange((next ?? []).map((o) => o.value))
            break
          default:
            break
        }
      },
    })

  const selectedValues = useMemo(() => new Set(value), [value])
  const preventDropdownKeys = inputValue.length > 0

  const {
    isOpen,
    highlightedIndex,
    getLabelProps,
    getInputProps,
    getMenuProps,
    getItemProps,
    getToggleButtonProps,
    closeMenu,
  } = useCombobox<ComboboxOption>({
    inputId: id,
    items,
    inputValue,
    // Selection lives in useMultipleSelection. Picking clears the query and
    // keeps the menu open, with the picked value still highlighted after filtering.
    selectedItem: null,
    itemToString: (item) => item?.label ?? '',
    itemToKey: item => item?.value ?? null,
    isItemDisabled: (item) => disabled || Boolean(item.disabled),
    onInputValueChange: ({ inputValue: next, type }) => {
      if (type === useCombobox.stateChangeTypes.InputChange) {
        setInputValue(next ?? '')
        onInputChange?.(next ?? '')
      }
    },
    stateReducer: (state, { changes, type }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return { ...changes, inputValue: '', isOpen: true,
            highlightedIndex: changes.selectedItem
              ? options.findIndex(option => option.value === changes.selectedItem?.value)
              : state.highlightedIndex }
        case useCombobox.stateChangeTypes.InputBlur:
          return { ...changes, selectedItem: null }
        default:
          return changes
      }
    },
    onStateChange: ({ type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem && !selectedItem.disabled) {
            const has = selectedValues.has(selectedItem.value)
            const next = has
              ? value.filter((v) => v !== selectedItem.value)
              : [...value, selectedItem.value]
            onChange(next)
            setInputValue('')
          }
          break
        default:
          break
      }
    },
  })
  const expanded = isOpen && !disabled
  const surface = useMenuSurface(expanded, items, highlightedIndex)
  useEffect(() => { if (disabled) closeMenu() }, [disabled, closeMenu])
  const inputProps = getInputProps(getDropdownProps({ preventKeyAction: preventDropdownKeys || disabled, disabled }))

  return (
    <Field className="field">
      <Label className="field__lbl" {...getLabelProps()}>
        {label}
      </Label>
      {hint && <Field.Description id={`${id ?? baseId}-hint`} className="field__hint">{hint}</Field.Description>}

      {/* Let the dropdown's Backspace remove the last chip only when the input
          is empty; while the user is typing a filter, Backspace edits the text
          instead of deleting a selection. */}
      <div ref={surface.anchorRef} className={clsx('cbx', 'cbx--multi', error && 'cbx--invalid')}>
        {selectedItems.map((item, index) => (
          <Chip.Removable
            key={item.value}
            className="cbx__chip"
            disabled={disabled}
            aria-label={message(locale, 'remove', { label: item.label })}
            {...getSelectedItemProps({ selectedItem: item, index })}
            onClick={event => {
              event.stopPropagation()
              removeSelectedItem(item)
              surface.anchorRef.current?.querySelector('input')?.focus()
            }}
          >
            {item.label}
          </Chip.Removable>
        ))}
        <Input
          className="cbx__input"
          placeholder={selectedItems.length === 0 ? placeholder : undefined}
          aria-required={required || undefined}
          aria-invalid={error ? 'true' : undefined}
          {...inputProps}
          onKeyDown={event => { escapeKey(event, expanded); inputProps.onKeyDown?.(event) }}
        />
        <Button variant="tertiary" icon
          type="button"
          className="cbx__toggle"
          {...getToggleButtonProps({ disabled, 'aria-label': message(locale, expanded ? 'close' : 'open', { label }) })}
        >
          <Icon name="chevronD" size={18} aria-hidden="true" />
        </Button>
      </div>

      {createPortal(
        <Dropdown asChild open={expanded} placement="none" onToggle={surface.place} style={menuStyle}>
          <ul className="dd__menu cbx__menu" {...getMenuProps({ ref: surface.menuRef, 'aria-multiselectable': true })}>
            {expanded && items.map((item, index) => {
              const selected = selectedValues.has(item.value)
              return (
                <Button asChild variant="tertiary" key={item.value}>
                  <li
                    className={clsx(
                      'dd__item',
                      index === highlightedIndex && 'dd__item--highlight',
                      selected && 'dd__item--active',
                      item.disabled && 'dd__item--disabled',
                    )}
                    {...getItemProps({ item, index })}
                    aria-selected={selected}
                    data-active={index === highlightedIndex || undefined}
                  >
                    <span className="dd__item-text">{item.label}</span>
                  </li>
                </Button>
              )
            })}
          </ul>
        </Dropdown>, surface.host,
      )}
      <span className="sr-only" role="status">
        {expanded && message(locale, 'results', { count: items.length })} {message(locale, 'selected', { count: value.length })}
      </span>

      {error && (
        <ValidationMessage id={errorId} className="field__err" role="alert">
          {error}
        </ValidationMessage>
      )}
    </Field>
  )
}
